import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/config';
import { useAuth } from './AuthContext';
import { isSuperAdmin } from '../lib/permissions';

const ImpersonationContext = createContext(null);

// Storage key for persisting impersonation state
const IMPERSONATION_STORAGE_KEY = 'fmiq_impersonation';

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};

export const ImpersonationProvider = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [readOnlyMode, setReadOnlyMode] = useState(true);
  const [impersonatableUsers, setImpersonatableUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const API = `${API_URL}/api`;

  // Check if current user can impersonate
  const canImpersonate = user && isSuperAdmin(user.role);

  // Restore impersonation state from session storage on mount
  useEffect(() => {
    if (!canImpersonate || !token) return;
    
    try {
      const stored = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (stored) {
        const { targetUser: storedTarget, readOnlyMode: storedReadOnly } = JSON.parse(stored);
        if (storedTarget) {
          setTargetUser(storedTarget);
          setIsImpersonating(true);
          const restoredReadOnly = storedReadOnly ?? true;
          setReadOnlyMode(restoredReadOnly);
          // Restore the impersonation header
          axios.defaults.headers.common['X-Impersonate-User'] = storedTarget.id;
          // Restore write header based on read-only state
          if (!restoredReadOnly) {
            axios.defaults.headers.common['X-Impersonate-Write'] = 'true';
          } else {
            delete axios.defaults.headers.common['X-Impersonate-Write'];
          }
        }
      }
    } catch (error) {
      console.error('Failed to restore impersonation state:', error);
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [canImpersonate, token]);

  // Fetch list of users that can be impersonated
  const fetchImpersonatableUsers = useCallback(async () => {
    if (!canImpersonate || !token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/impersonation/users`);
      setImpersonatableUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch impersonatable users:', error);
      setImpersonatableUsers([]);
    } finally {
      setLoading(false);
    }
  }, [API, canImpersonate, token]);

  // Start impersonating a user
  const startImpersonation = useCallback(async (userId) => {
    if (!canImpersonate) return false;

    try {
      const response = await axios.get(`${API}/impersonation/context/${userId}`);
      const { target_user } = response.data;
      
      setTargetUser(target_user);
      setIsImpersonating(true);
      setReadOnlyMode(true); // Always start in read-only mode
      
      // Add impersonation header to all future requests
      axios.defaults.headers.common['X-Impersonate-User'] = userId;
      // Ensure write header is NOT set (read-only by default)
      delete axios.defaults.headers.common['X-Impersonate-Write'];
      
      // Persist to session storage so it survives page reloads
      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify({
        targetUser: target_user,
        readOnlyMode: true
      }));
      
      return true;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      return false;
    }
  }, [API, canImpersonate]);

  // Stop impersonating
  const stopImpersonation = useCallback(async () => {
    try {
      // Log the end of impersonation
      if (targetUser) {
        await axios.post(`${API}/impersonation/end`, null, {
          params: { target_user_id: targetUser.id }
        });
      }
    } catch (error) {
      console.error('Failed to log impersonation end:', error);
    }

    // Remove impersonation headers
    delete axios.defaults.headers.common['X-Impersonate-User'];
    delete axios.defaults.headers.common['X-Impersonate-Write'];
    
    // Clear session storage
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    
    setTargetUser(null);
    setIsImpersonating(false);
    setReadOnlyMode(true);
  }, [API, targetUser]);

  // Toggle read-only mode
  const toggleReadOnlyMode = useCallback(() => {
    setReadOnlyMode(prev => {
      const newValue = !prev;
      // Sync write-access header with backend
      if (!newValue) {
        // Full Access mode — allow writes
        axios.defaults.headers.common['X-Impersonate-Write'] = 'true';
      } else {
        // Read-only mode — block writes
        delete axios.defaults.headers.common['X-Impersonate-Write'];
      }
      // Update session storage
      if (targetUser) {
        sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify({
          targetUser,
          readOnlyMode: newValue
        }));
      }
      return newValue;
    });
  }, [targetUser]);

  // Clear impersonation state on logout (but NOT during initial load)
  useEffect(() => {
    // Only clear if auth is done loading AND user is null (actual logout)
    // Don't clear during initial load when user is temporarily null
    if (!authLoading && !user) {
      setIsImpersonating(false);
      setTargetUser(null);
      setReadOnlyMode(true);
      delete axios.defaults.headers.common['X-Impersonate-User'];
      delete axios.defaults.headers.common['X-Impersonate-Write'];
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [user, authLoading]);

  // Fetch impersonatable users when user logs in
  useEffect(() => {
    if (canImpersonate) {
      fetchImpersonatableUsers();
    }
  }, [canImpersonate, fetchImpersonatableUsers]);

  const value = {
    // State
    isImpersonating,
    targetUser,
    readOnlyMode,
    impersonatableUsers,
    loading,
    canImpersonate,
    // v1.3.35.33 — Effective role for frontend gates. When a
    // super-admin is impersonating, the frontend role guards
    // (vendor redirect, sidebar hide, etc.) must consult the
    // TARGET user's role instead of the caller's own role — else
    // "View as Vendor" would still show the founder's UI. Null
    // when not impersonating; callers should fall back to
    // AuthContext.user.role in that case.
    effectiveRole: (isImpersonating && targetUser)
      ? (targetUser.role || null)
      : null,
    
    // Actions
    startImpersonation,
    stopImpersonation,
    toggleReadOnlyMode,
    fetchImpersonatableUsers,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationContext;
